/**
 * API Client with Authentication
 * 
 * Handles API requests with automatic authentication token injection
 */

const API_BASE = '/api';

/**
 * Get the authentication token from session storage
 */
function getAuthToken(): string | null {
  try {
    const sessionData = localStorage.getItem('patterniq_auth_session');
    if (!sessionData) return null;

    const session = JSON.parse(sessionData);
    
    // Check if session is expired
    if (session.expiresAt && session.expiresAt < Date.now()) {
      localStorage.removeItem('patterniq_auth_session');
      return null;
    }

    // Return the JWT token from the session
    // The token is the credential from Google OAuth
    return session.token || null;
  } catch (error) {
    console.error('Error getting auth token:', error);
    return null;
  }
}

/**
 * API client with automatic authentication
 */
export const apiClient = {
  /**
   * GET request with authentication
   */
  get: async <T>(endpoint: string): Promise<T> => {
    const token = getAuthToken();
    const headers: HeadersInit = {
      'Content-Type': 'application/json'
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      method: 'GET',
      headers,
      credentials: 'include'
    });

    if (response.status === 401) {
      // Token expired or invalid - redirect to login
      localStorage.removeItem('patterniq_auth_session');
      window.location.href = '/login';
      throw new Error('Unauthorized - Please log in again');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(error.message || `API Error: ${response.statusText}`);
    }

    return response.json();
  },

  /**
   * POST request with authentication
   */
  post: async <T>(endpoint: string, data?: any): Promise<T> => {
    const token = getAuthToken();
    const headers: HeadersInit = {
      'Content-Type': 'application/json'
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      method: 'POST',
      headers,
      body: data ? JSON.stringify(data) : undefined,
      credentials: 'include'
    });

    if (response.status === 401) {
      localStorage.removeItem('patterniq_auth_session');
      window.location.href = '/login';
      throw new Error('Unauthorized - Please log in again');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(error.message || `API Error: ${response.statusText}`);
    }

    return response.json();
  },

  /**
   * PUT request with authentication
   */
  put: async <T>(endpoint: string, data: any): Promise<T> => {
    const token = getAuthToken();
    const headers: HeadersInit = {
      'Content-Type': 'application/json'
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(data),
      credentials: 'include'
    });

    if (response.status === 401) {
      localStorage.removeItem('patterniq_auth_session');
      window.location.href = '/login';
      throw new Error('Unauthorized - Please log in again');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(error.message || `API Error: ${response.statusText}`);
    }

    return response.json();
  },

  /**
   * PATCH request with authentication
   */
  patch: async <T>(endpoint: string, data: any): Promise<T> => {
    const token = getAuthToken();
    const headers: HeadersInit = {
      'Content-Type': 'application/json'
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      method: 'PATCH',
      headers,
      body: JSON.stringify(data),
      credentials: 'include'
    });

    if (response.status === 401) {
      localStorage.removeItem('patterniq_auth_session');
      window.location.href = '/login';
      throw new Error('Unauthorized - Please log in again');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(error.message || `API Error: ${response.statusText}`);
    }

    return response.json();
  },

  /**
   * DELETE request with authentication
   */
  delete: async <T>(endpoint: string): Promise<T> => {
    const token = getAuthToken();
    const headers: HeadersInit = {
      'Content-Type': 'application/json'
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      method: 'DELETE',
      headers,
      credentials: 'include'
    });

    if (response.status === 401) {
      localStorage.removeItem('patterniq_auth_session');
      window.location.href = '/login';
      throw new Error('Unauthorized - Please log in again');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(error.message || `API Error: ${response.statusText}`);
    }

    return response.json();
  }
};
